import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { HOST_PLATFORMS } from 'app/shared/consts';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { useUser } from 'app/shared/contexts/user';
import { useMatchedRoute } from 'app/shared/hooks/use-matched-route';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { useTrackPageImpression } from 'app/shared/hooks/use-track-page-impression';

interface UsePageImpressionParams {
  /** Page-specific dimensions. Undefined values are dropped rather than sent as `"undefined"`. */
  slugs?: Record<string, string | undefined>;
  /** Hold the impression until the page's own data has resolved, so its slugs aren't half-empty. */
  enabled?: boolean;
}

/**
 * Emits one `track_v2_web_page_impression` per distinct view — the pathname AND its slugs. Switching
 * segment or filter therefore counts as a new impression, because a different set of activity becomes
 * visible; the caller gates on `enabled` so it lands when that content is actually on screen rather
 * than while the pane is still loading.
 *
 * Pages call this themselves rather than a router-level observer doing it, because a receipt's
 * `activity_type` only exists once its booking has loaded — something the router can't know.
 *
 * Waits on `isHostInfoSettled` rather than on `hostInfo` itself, so a host that answers with a
 * malformed payload still reports (as mobile) instead of going dark.
 */
export const usePageImpression = ({ slugs = {}, enabled = true }: UsePageImpressionParams = {}): void => {
  const trackImpression = useTrackPageImpression();
  const route = useMatchedRoute();
  const { pathname } = useLocation();
  const { buildingUuid } = useOpenParams();
  const { hostPlatform, isHostInfoSettled } = useSdkHostInfo();
  const { user } = useUser();
  const userUuid = user?.uuid ?? '';

  const firedForViewRef = useRef<string | null>(null);
  const slugsRef = useRef(slugs);
  slugsRef.current = slugs;

  const slugsKey = useMemo(() => JSON.stringify(slugs), [slugs]);
  // The view identity: same pathname with different slugs (a different segment/filter pane) is a
  // different view, and re-fires.
  const viewKey = `${pathname}|${slugsKey}`;

  useEffect(() => {
    if (!enabled || !isHostInfoSettled || !userUuid) {
      return;
    }

    if (firedForViewRef.current === viewKey) {
      return;
    }

    firedForViewRef.current = viewKey;

    const pageSlugs = Object.entries(slugsRef.current).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    trackImpression({
      route,
      resolved_route: pathname,
      // `building_uuid` is required, not decorative: dbt inner-joins the building dimension on it, so an
      // impression without one is dropped outright.
      slugs: {
        building_uuid: buildingUuid,
        host_platform: hostPlatform ?? HOST_PLATFORMS.MOBILE,
        ...pageSlugs,
      },
    });
  }, [enabled, isHostInfoSettled, userUuid, viewKey, pathname, route, buildingUuid, hostPlatform, trackImpression]);
};
